/**
 * PaymentReceiptPrint — A4 print page for a payment / final invoice.
 * Route: /invoice/print/:id
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { paymentsAPI, patientsAPI } from '../lib/api';
import { PrintShell, PrintField, PrintSection, fmtDateTime, fmtINR } from '../components/print/PrintShell';

const METHOD_LABEL = {
  cash: 'Cash', card: 'Card', upi: 'UPI', bank_transfer: 'Bank Transfer',
  netbanking: 'Net Banking', insurance: 'Insurance', cheque: 'Cheque', wallet: 'Wallet',
};

const PaymentReceiptPrint = () => {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    paymentsAPI.get(id)
      .then(async res => {
        if (!mounted) return;
        setRecord(res.data);
        if (res.data?.patient_id) {
          try {
            const p = await patientsAPI.get(res.data.patient_id);
            if (mounted) setPatient(p.data);
          } catch { /* swallow */ }
        }
      })
      .catch(() => { if (mounted) setRecord(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id]);

  const r = record || {};
  const p = patient || {};
  const splits = Array.isArray(r.splits) && r.splits.length
    ? r.splits
    : [{ method: r.payment_method || 'cash', amount: r.amount, reference: '' }];
  const baseAmount = r.base_amount ?? r.amount;
  const taxAmount = r.tax_amount ?? 0;
  const grandTotal = r.amount;

  return (
    <PrintShell
      title="Payment Receipt / Tax Invoice"
      subtitle="Official payment receipt issued by the hospital."
      loading={loading}
      notFound={!loading && !record}
      hospitalId={r.hospital_id}
      docRef={r.receipt_number || `INV-${(r.id || '').slice(0, 8).toUpperCase()}`}
      statusPill={{ label: 'Paid', color: '#10b981' }}>
      <PrintSection title="Patient Details">
        <div className="grid grid-cols-2 gap-4 text-xs">
          <PrintField label="Patient Name" value={`${p.title ? p.title + ' ' : ''}${p.first_name || ''} ${p.last_name || ''}`.trim() || r.patient_name} />
          <PrintField label="UHID" value={p.uhid || r.patient_uhid} mono />
          {(p.age || p.gender) && <PrintField label="Age / Gender" value={[p.age, p.gender].filter(Boolean).join(' / ')} />}
          {p.phone && <PrintField label="Phone" value={p.phone} mono />}
          {p.address && <PrintField label="Address" value={p.address} />}
          {p.is_vip && <PrintField label="Membership" value="⭐ VIP Patient" />}
        </div>
      </PrintSection>

      <PrintSection title="Service Details">
        <table className="w-full text-xs border border-slate-200 rounded overflow-hidden">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-2 py-1.5 text-left">Description</th>
              <th className="px-2 py-1.5 text-right">Base</th>
              <th className="px-2 py-1.5 text-right">Tax</th>
              <th className="px-2 py-1.5 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-slate-100">
              <td className="px-2 py-1.5">{r.service_name || r.description || 'Consultation / Service'}</td>
              <td className="px-2 py-1.5 text-right font-mono">{fmtINR(baseAmount)}</td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-500">{fmtINR(taxAmount)}</td>
              <td className="px-2 py-1.5 text-right font-mono font-semibold">{fmtINR(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
      </PrintSection>

      <PrintSection title="Payment Breakdown">
        <table className="w-full text-xs border border-slate-200 rounded overflow-hidden">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-2 py-1.5 text-left">Method</th>
              <th className="px-2 py-1.5 text-left">Reference</th>
              <th className="px-2 py-1.5 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {splits.map((s, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-2 py-1.5 font-medium">{METHOD_LABEL[s.method] || s.method || 'Cash'}</td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{s.reference || '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono">{fmtINR(s.amount)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-300 bg-slate-50">
              <td colSpan={2} className="px-2 py-2 font-bold text-right">Grand Total</td>
              <td className="px-2 py-2 text-right font-mono font-bold">{fmtINR(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
      </PrintSection>

      <div className="mt-3 mb-6 inline-block border-2 border-emerald-500 text-emerald-700 font-bold px-3 py-1 text-xs rounded">
        PAID IN FULL
      </div>

      <div className="grid grid-cols-2 gap-4 text-xs mb-6">
        <PrintField label="Collected By" value={r.collected_by_name} />
        <PrintField label="Receipt Date" value={fmtDateTime(r.created_at)} />
      </div>

      <div className="grid grid-cols-2 gap-12 mt-8 pt-6 border-t border-slate-200 text-xs">
        <div>
          <div className="h-10 border-b border-slate-400 mb-2 inline-block w-40" />
          <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Cashier Signature</p>
        </div>
        <div className="text-right">
          <div className="h-10 border-b border-slate-400 mb-2 inline-block w-40" />
          <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Patient / Payer</p>
        </div>
      </div>
    </PrintShell>
  );
};

export default PaymentReceiptPrint;
